import React from "react";
import { useLookups } from "../context/LookupOptionsContext";
import {
  emptyContractItemLine,
  isOnArrivalDirectPurchase,
  qualitySelectOptions,
} from "../lib/purchaseContractOptions";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Plus, Trash2 } from "lucide-react";

export default function PurchaseContractItemLines({
  form,
  setForm,
  linkableGatePasses = [],
  compact = false,
}) {
  const { optionsFor } = useLookups();
  const onArrivalDirect = isOnArrivalDirectPurchase(form);
  const paddyVarieties = optionsFor("paddy_variety");
  const qualityOptions = qualitySelectOptions(
    form.gate_pass_id,
    linkableGatePasses,
    paddyVarieties,
    (form.items || []).map((line) => line.quality),
  );
  const items = form.items || [];

  const setItems = (next) => setForm({ ...form, items: next });

  const updateLine = (index, patch) => {
    setItems(items.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const addLine = () => setItems([...items, emptyContractItemLine()]);

  const removeLine = (index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const lineHasAnyValue = (line) =>
    Boolean(
      String(line.quality || "").trim()
        || String(line.quantity || "").trim()
        || (line.price !== "" && line.price != null && line.price !== undefined),
    );

  /** Only filled rows (or the sole row) use HTML required — empty extra rows are skipped on save. */
  const rowRequired = (line) => items.length === 1 || lineHasAnyValue(line);

  return (
    <div className="md:col-span-3 space-y-4 rounded-lg border border-teal-200 bg-teal-50/40 p-4" data-testid="pac-item-lines">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-teal-900">
            {compact ? "Varieties on this truck" : "Item lines (varieties)"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {compact
              ? "One contract per variety — same farmer, gate pass, and preset terms. Empty rows are skipped."
              : "One purchase contract per line — same party and terms; weighbridge can allocate the truck across these contracts."}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addLine} className="shrink-0" data-testid="pac-add-line">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add variety
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((line, index) => {
          const required = rowRequired(line);
          return (
          <div
            key={line._key || index}
            className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end rounded-md border bg-card/80 p-3"
            data-testid={`pac-line-${index}`}
          >
            <div className="md:col-span-1 text-xs font-medium text-muted-foreground pb-2">#{index + 1}</div>

            <div className="md:col-span-4">
              <LineLabel label="Quality" required={required} />
              <Select
                value={line.quality || "_none"}
                onValueChange={(v) => updateLine(index, { quality: v === "_none" ? "" : v })}
                disabled={onArrivalDirect && !form.gate_pass_id}
              >
                <SelectTrigger data-testid={`pac-line-quality-${index}`}>
                  <SelectValue
                    placeholder={
                      onArrivalDirect && !form.gate_pass_id ? "Select gate pass first" : "Select quality…"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none" disabled>
                    {onArrivalDirect && !form.gate_pass_id ? "Select gate pass first" : "Select quality…"}
                  </SelectItem>
                  {qualityOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!onArrivalDirect && (
              <div className="md:col-span-3">
                <LineLabel label="Quantity (qtl)" required={required} />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={line.quantity}
                  onChange={(e) => updateLine(index, { quantity: e.target.value })}
                  required={required}
                  data-testid={`pac-line-qty-${index}`}
                />
              </div>
            )}

            <div className={onArrivalDirect ? "md:col-span-6" : "md:col-span-3"}>
              <LineLabel label="Rate (₹/qtl)" required={required} />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={line.price}
                onChange={(e) => updateLine(index, { price: e.target.value })}
                required={required}
                data-testid={`pac-line-price-${index}`}
              />
            </div>

            <div className="md:col-span-1 flex justify-end pb-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-red-600"
                onClick={() => removeLine(index)}
                disabled={items.length <= 1}
                aria-label="Remove line"
                data-testid={`pac-line-remove-${index}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function LineLabel({ label, required }) {
  return (
    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
  );
}
